// How each stored exposure `kind` is presented. Only `credential` findings are
// breaches; anything not listed is "unclassified" and must never render with
// breach wording (password flags, records exposed, reuse guidance).
const FINDING_TYPES = {
    credential: 'breach',
    email_security: 'email_security',
    attack_surface: 'attack_surface',
};

export function findingType(kind) {
    const t = FINDING_TYPES[kind];
    if (!t) {
        console.warn('Unmapped exposure finding kind rendered as unclassified:', kind);
        return 'unclassified';
    }
    return t;
}

export const FINDING_TYPE_LABEL = {
    breach: null,               // breach findings keep their source-specific tag
    email_security: 'email security',
    attack_surface: 'attack surface',
    unclassified: 'unclassified',
};

export const CT_SENSITIVE_WORDS = ['portal', 'admin', 'vpn', 'remote', 'dev', 'staging', 'test', 'mail', 'sso', 'login'];

export function isSensitiveSubdomain(name) {
    const label = String(name).toLowerCase().split('.')[0];
    return CT_SENSITIVE_WORDS.some(w => label.includes(w));
}

export function ctView(f) {
    const subs = (f.sample_identifiers || []).filter(x => typeof x !== 'object').map(String);
    const sensitive = subs.filter(isSensitiveSubdomain);
    const other = subs.filter(x => !sensitive.includes(x));
    const total = f.record_count || subs.length;
    let intro = `${total} subdomain(s) of this domain appear in Certificate Transparency (CT) logs. ` +
        'CT logs are public by design: every TLS certificate issued is published so it can be audited. ' +
        'This is discovery, not leakage. Nothing was breached and no credentials or records are exposed, ' +
        'but it gives an attacker a ready map of internet-facing services.';
    if (sensitive.length) {
        intro += ` ${sensitive.length} name(s) suggest staff-only or non-production services ` +
            '(e.g. admin, VPN, staging, login), which are the usual first targets.';
    }
    const recommendations = [
        'Confirm the company owns and still uses every subdomain listed.',
        'Remove DNS records for anything unused; a record pointing at a decommissioned cloud service can be claimed by an attacker (subdomain takeover).',
        'Require MFA on every login, admin, VPN, mail and SSO service.',
        'Keep exposed services patched and rate-limit their login endpoints.',
        'Put staff-only services behind a VPN or an IP allowlist rather than on the open internet.',
    ];
    return { intro, sensitive, other, total, recommendations };
}

export function dmarcView(f) {
    const posture = (f.sample_identifiers || []).find(x => x && typeof x === 'object') || null;
    const policy = posture ? posture.dmarc_policy : null;
    const pct = posture ? posture.dmarc_pct : null;
    const present = posture ? posture.dmarc_present : null;
    let meaning;
    if (!posture) {
        meaning = 'Email authentication (SPF/DMARC) for this domain is weak or incomplete; see the issues listed. Re-run the screen for the full policy breakdown.';
    } else if (!present) {
        meaning = 'There is no DMARC record, so receiving mail servers have no instruction to reject mail that falsely claims to come from this domain.';
    } else if (!policy || policy === 'none') {
        meaning = 'DMARC is set to p=none: monitoring only. Spoofed mail claiming to be from this domain is still delivered to inboxes.';
    } else if (policy === 'quarantine') {
        meaning = 'DMARC is set to p=quarantine: spoofed mail is usually sent to spam rather than rejected, so some can still reach and fool staff or customers.';
    } else {
        meaning = 'DMARC is set to p=reject: receiving servers are told to refuse spoofed mail.';
    }
    if (present && pct !== null && pct !== undefined && pct < 100) {
        meaning += ` The policy applies to only ${pct}% of mail (pct=${pct}); the rest is not enforced.`;
    }
    const informational = f.severity === 'low';
    meaning += informational
        ? ' This is informational: the domain is well protected against direct spoofing.'
        : " The practical risk is impersonation: fake invoices, changed bank details and payment requests that appear to come from the company's own domain.";
    const facts = [];
    if (posture) {
        facts.push(['DMARC policy (p=)', present ? (policy || 'none') : 'no record']);
        if (present) {
            facts.push(['Coverage (pct=)', String(pct ?? 100)]);
            facts.push(['Aggregate reporting (rua)', posture.dmarc_rua ? 'configured' : 'not configured']);
        }
        facts.push(['SPF', posture.spf_present ? (posture.spf_all || 'no all mechanism') : 'no record']);
    }
    const recommendations = [
        'Review the DMARC aggregate (rua) reports to see every service sending as this domain.',
        'Align legitimate senders (mail provider, CRM, invoicing, marketing tools) with SPF and DKIM.',
        'Move to p=reject with pct=100 once legitimate mail passes.',
        'End the SPF record with -all (hard fail) rather than ~all.',
        'Have finance verify any change of bank details by phone on a known number, never by replying to the email that requested it.',
    ];
    return { meaning, facts, recommendations, informational };
}

export function severityLabel(f) {
    return findingType(f.kind) === 'email_security' && f.severity === 'low' ? 'informational' : f.severity;
}
